import React, { ChangeEvent, useCallback } from 'react';
import { styled } from '@mui/material';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import ImageIcon from '@mui/icons-material/Image';
import { project } from '@/actions';
import Layout from '../Layout';
import { FormButton, FormSubTitle, CustomTextField, GroupLabel } from './common.controls';
import Revit3DViewControl from './Revit3DViewControl';
import { HintedIcon, ImageField } from '@/components';
import CustomCheckbox from './CustomCheckbox';
import t from '@/utils/translate';
import { showMessage } from '@/actions/common';
import PageTitle from '@/components/PageTitle';

// @ts-ignore: Unreachable code error
const LOGO_SIZE_LIMIT = +import.meta.env.VITE_AVA_SIZE_LIMIT_KB || 512;

const Field = styled('div')({
  display: 'flex',
  gap: '15px',
  flexDirection: 'column',
});

const FormRow = styled('div')({
  padding: '20px 0',
  display: 'flex',
  justifyContent: 'center',
});
const FormColumn = styled('div')({
  width: '50%',
  paddingTop: '40px',
  display: 'flex',
  justifyContent: 'center',
});
const FieldsGroup = styled('div')({
  width: '390px',
  gap: '30px',
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  alignItems: 'flex-start',
});

const CustomProjectNameField = styled(CustomTextField)({
  '& .MuiInputBase-root': {
    width: '390px',
  },
});

const TitleDescription = styled('div')({
  marginTop: '10px',
  fontSize: '13px',
  fontWeight: 400,
  color: '#6B778C'
});

type FormDataType = {
  name: string,
  revit3DView: string | null,
  importRevitEdges: boolean,
  logoFile: File | null,
  logoPath: string | null,
};

export default () => {
  const navigate = useNavigate();

  const formik = useFormik<FormDataType>({
    initialValues: {
      name: '',
      revit3DView: null,
      importRevitEdges: true,
      logoPath: null,
      logoFile: null
    },
    validate: (values: FormDataType) => {
      const errors: Record<string, string> = {};

      if (!values.name) {
        errors.name = t('project.validation.noName')
      } else if (values.name.length > 256) {
        errors.name = t('project.validation.nameTooLong')
      }

      return errors;
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async ({ name, revit3DView, importRevitEdges, logoFile }, helpers) => {
      if (!revit3DView) {
        revit3DView = null;
      }
      const pid = await project.create({
        name,
        ...(revit3DView ? { revit3DView } : {}),
        importRevitEdges,
      });
      if (pid && logoFile) {
        if (logoFile.size > LOGO_SIZE_LIMIT * 1024) {
          showMessage(t('project.error.thumbSize', { limit: LOGO_SIZE_LIMIT }), 'error');
        } else {
          await project.setLogo(pid, logoFile);
        }
      }
      navigate(`/project/${pid || ''}`);
    },
  });

  const onLogoSetCallback = useCallback((file: File) => {
    formik.setFieldValue('logoFile', file);
    formik.setFieldValue('logoPath', URL.createObjectURL(file));
  }, [ formik ]);

  const onLogoClearCallback = useCallback(() => {
    formik.setFieldValue('logoFile', null);
    formik.setFieldValue('logoPath', null);
  }, [ formik ]);

  const onRevit3DViewControlChange = useCallback((v: string | null) => {
    formik.setFieldValue('revit3DView', v)
  }, [ formik ]);

  return (<Layout>
    <PageTitle>Создание проекта</PageTitle>
    <form onSubmit={formik.handleSubmit}>
      <FormRow>
        <FormColumn style={{ borderRight: '1px solid #DFE1E6' }}>
          <ImageField
            title={t('project.title.thumbnail')}
            source={formik.values.logoPath}
            onSet={onLogoSetCallback}
            onClear={onLogoClearCallback}
            sizeV={246}
            sizeH={300}
            previewSizeV={140}
            previewSizeH={260}
            placeholder={
              <ImageIcon
                sx={{
                  fontSize: '93.35px',
                  color: '#DADADA',
                  verticalAlign: 'inherit'
                }}
              />
            }
          />
        </FormColumn>

        <FormColumn>
          <FieldsGroup>
            <CustomProjectNameField
              {...formik.getFieldProps('name')}
              autoFocus
              disabled={formik.isSubmitting}
              label={t('project.label.name')}
              error={formik.touched.name && !!formik.errors.name}
              helperText={formik.touched.name && formik.errors.name}
              variant="outlined"
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                formik.setFieldError('name', '');
                formik.setFieldValue('name', e.target.value);
              }}
            />

            <div>
              <FormSubTitle>{t('project.settings.title.import')}</FormSubTitle>
              <TitleDescription>
                {t('project.settings.hint.immutable', true)}
              </TitleDescription>
            </div>

            <Field>
              <GroupLabel>
                {t('project.settings.label.views')}
                <HintedIcon>{t('project.settings.label.views')}</HintedIcon>
              </GroupLabel>
              
              <Revit3DViewControl
                {...formik.getFieldProps('revit3DView')}
                views={[]}
                value={formik.values.revit3DView}
                disabled={formik.isSubmitting}
                error={formik.touched.revit3DView && !!formik.errors.revit3DView}
                helperText={formik.touched.revit3DView && formik.errors.revit3DView}
                onValueChange={onRevit3DViewControlChange}
                specificViewOptionLabel={t('project.settings.option.specificView')}
                gap='8px'
              />
            </Field>
{/*
            <Field>
              <GroupLabel>{t('project.label.settings.rendering')}</GroupLabel>
              <CustomCheckbox
                label={t('project.settings.label.edges')}
                hint={t('project.settings.label.edges')}
                checked={formik.values.importRevitEdges}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => { formik.setFieldValue('importRevitEdges', event.target.checked) }}
              />
            </Field>
*/}
          </FieldsGroup>
        </FormColumn>
      </FormRow>
      <FormRow>
        <FormButton
          type="submit"
          disabled={formik.isSubmitting || !formik.dirty}
          variant="contained"
          loading={formik.isSubmitting}
        >
          {t('project.action.create')}
        </FormButton>
      </FormRow>
    </form>
  </Layout>);
}
